/**
 * Handige database queries
 * Dit maakt het leven wat makkelijker
 */

import { databaseClient } from './index';
import type { Product, ProductDatabaseRow } from '../models';
import { maakApplicatieFout, isApplicatieFout } from '../models/AppError';

/**
 * Database rij omzetten naar Product model.
 * Beetje mappen enzo. De database gebruikt Nederlandse kolomnamen met spaties (bah),
 * maar in onze code willen we nette Engelse camelCase namen.
 */
export function transformDatabaseRow(row: ProductDatabaseRow): Product {
  try {
    return {
      nummer: row.Nummer,
      product: row.Product,
      merk: row.Merk,
      categorie: row.Categorie,
      milieuRating: row['Milieu Rating (A+ tot E-)'],
      waaromRating: row['Waarom deze rating? (Impact op water/aquatisch leven)'],
      waaromRating_en: row.WaaromRating_EN,
      reason1: row.Reason_1,
      reason1_en: row.Reason_1_EN,
      reason2: row.Reason_2,
      reason2_en: row.Reason_2_EN,
      reason3: row.Reason_3,
      reason3_en: row.Reason_3_EN,
      reason4: row.Reason_4,
      reason4_en: row.Reason_4_EN,
      barcode: row.Barcode,
      imageUrl: row.ImageUrl,
      ph: row.Ph,
      tds: row.Tds,
      turbidity: row.Turbidity,
    };
  } catch (error) {
    console.error('Fout bij omzetten database rij:', error);
    throw maakApplicatieFout(
      'database_query',
      'Kon database rij niet omzetten naar Product model',
      false,
      undefined,
      error instanceof Error ? error : undefined,
    );
  }
}

/**
 * Zoek op ID
 */
export async function getProductById(id: number): Promise<Product | null> {
  try {
    const row = await databaseClient.getProductById(id);
    return row ? transformDatabaseRow(row) : null;
  } catch (error) {
    console.error('Fout bij ophalen product op ID:', error);

    // Als het al een AppError is, gewoon doorgooien
    if (isApplicatieFout(error)) {
      throw error;
    }

    // Anders inpakken als AppError
    throw maakApplicatieFout(
      'database_query',
      `Kon product met ID ${id} niet ophalen`,
      true,
      () => getProductById(id),
      error instanceof Error ? error : undefined,
    );
  }
}

/**
 * Zoek op barcode (die cijfertjes)
 */
export async function getProductByBarcode(
  barcode: string,
): Promise<Product | null> {
  try {
    const row = await databaseClient.getProductByBarcode(barcode);
    return row ? transformDatabaseRow(row) : null;
  } catch (error) {
    console.error('Fout bij ophalen product op barcode:', error);

    // Als het al een AppError is, gewoon doorgooien
    if (isApplicatieFout(error)) {
      throw error;
    }

    // Anders inpakken als AppError
    throw maakApplicatieFout(
      'database_query',
      `Kon product met barcode ${barcode} niet ophalen`,
      true,
      () => getProductByBarcode(barcode),
      error instanceof Error ? error : undefined,
    );
  }
}

/**
 * Zoeken op naam of merk
 */
export async function searchProducts(searchTerm: string): Promise<Product[]> {
  try {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return [];
    }

    const rows = await databaseClient.searchProducts(searchTerm.trim());
    return rows.map(transformDatabaseRow);
  } catch (error) {
    console.error('Fout bij zoeken producten:', error);

    // Als het al een AppError is, gewoon doorgooien
    if (isApplicatieFout(error)) {
      throw error;
    }

    // Anders inpakken als AppError
    throw maakApplicatieFout(
      'database_query',
      `Kon producten niet vinden met zoekterm "${searchTerm}"`,
      true,
      () => searchProducts(searchTerm),
      error instanceof Error ? error : undefined,
    );
  }
}

/**
 * Alles van 1 categorie ophalen
 */
export async function getProductsByCategory(
  category: string,
): Promise<Product[]> {
  try {
    const rows = await databaseClient.getProductsByCategory(category);
    return rows.map(transformDatabaseRow);
  } catch (error) {
    console.error('Fout bij ophalen producten op categorie:', error);

    // Als het al een AppError is, gewoon doorgooien
    if (isApplicatieFout(error)) {
      throw error;
    }

    // Anders inpakken als AppError
    throw maakApplicatieFout(
      'database_query',
      `Kon producten met categorie "${category}" niet ophalen`,
      true,
      () => getProductsByCategory(category),
      error instanceof Error ? error : undefined,
    );
  }
}

/**
 * Willekeurige producten ophalen (discovery mode)
 */
export async function getRandomProducts(limit: number): Promise<Product[]> {
  try {
    const rows = await databaseClient.getRandomProducts(limit);
    return rows.map(transformDatabaseRow);
  } catch (error) {
    console.error('Fout bij ophalen willekeurige producten:', error);
    if (isApplicatieFout(error)) throw error;
    throw maakApplicatieFout(
      'database_query',
      'Kon geen willekeurige producten ophalen',
      false, // Niet automatisch opnieuw proberen
      () => getRandomProducts(limit),
      error instanceof Error ? error : undefined,
    );
  }
}


